import React, { useContext, useMemo, useState } from "react";
import { PageLayout, Separator, Text } from "components";
import {
  MeetingListCardStyle,
  TranscriptFilterContainerStyle,
  TranscriptStyle,
} from "../styles";
import { UserContext } from "context";
import { EActivityEvent, TranscriptsResponse } from "types";
import { recordActivity } from "services";
import { useNavigate } from "react-router-dom";
import { E_Route } from "utils/constants";
import DatePicker from "react-date-picker";
import "../styles/DatePicker.css";
import "../styles/Calendar.css";
import { CardBase, TranscriptListItem } from "../components";
import { Value } from "react-calendar/dist/cjs/shared/types";
import { CalendarBlankIcon, CloseIcon } from "components/icons";
import { css } from "@emotion/css";
import { useQuery } from "@apollo/client";
import { GET_TRANSCRIPTS } from "apollo";
import { format } from "date-fns";
import { useMeeting } from "features/meetings/hooks";

const next2Weeks = new Date();
next2Weeks.setDate(next2Weeks.getDate() + 14);

export const TranscriptListScreen = () => {
  const { state: userState } = useContext(UserContext);
  const [value, onDateChange] = useState<Value>();
  const { data: transcriptData, loading } = useQuery<TranscriptsResponse>(
    GET_TRANSCRIPTS,
    {
      //fetchPolicy: 'network-only',
      //pollInterval: demoMode ? undefined : 5000,
      variables: {
        date: value ? new Date(value.toString()).getTime() : undefined,
      },
    },
  );

  const {
    allMeetings,
    externalMeetings,
    loading: meetingLoading,
  } = useMeeting(value ? new Date(value.toString()).getTime() : undefined);

  const navigate = useNavigate();

  const handleClick = (
    id: string,
    hasTranscript: boolean,
    internal: boolean,
  ) => {
    if (userState?._id) {
      recordActivity({
        data: {
          user: userState?._id,
          event: hasTranscript
            ? EActivityEvent.TranscriptOpened
            : EActivityEvent.MeetingPrepClicked,
          source: "web",
          data: {
            transcriptId: id,
          },
        },
      });

      const url = hasTranscript
        ? `${E_Route.MeetingTranscript}/${id}` +
          (internal ? "?internal=yes" : "")
        : `${E_Route.Meeting}/${id}`;
      navigate(url);
    }
  };

  //const transcripts = transcriptData?.transcripts || [];

  const all = useMemo(() => {
    const result = [];
    const transcripts = transcriptData?.transcripts || [];
    // merge allMeetings and transcripts
    // sort by date
    const length = Math.max(allMeetings?.length || 0, transcripts?.length || 0);
    for (let i = 0; i < length; i++) {
      let item = allMeetings[i];
      if (item) {
        const meeting = externalMeetings?.find(
          (m) => m.meetingId === item.meetingId || m._id === item.meetingId,
        );
        const isInternal = !meeting;
        const tran = transcripts.find((t) => t.meetingId === item.meetingId);
        result.push({
          title: item.title,
          meetingDate: item.start ? new Date(item.start) : undefined,
          meetingId: item.meetingId,
          transcriptId: tran ? tran._id : undefined,
          hasTranscript: !!tran?._id,
          id: tran?._id || item?.id,
          disabled: !tran?._id && isInternal, // disable if no transcript and internal meeting
          internal: isInternal,
        });
      } else {
        const tran = transcripts[i];
        if (tran) {
          result.push({
            title: tran.meetingTitle || "Ad hoc meeting",
            meetingDate: new Date(tran.meetingDate || tran.created),
            meetingId: tran.meetingId,
            transcriptId: tran._id,
            hasTranscript: true,
            disabled: false,
            id: tran._id,
            internal: true,
          });
        }
      }
    }
    return result.sort((a, b) => {
      return (b.meetingDate?.getTime() || 0) - (a.meetingDate?.getTime() || 0);
    });
  }, [transcriptData?.transcripts, allMeetings, externalMeetings]);

  return (
    <PageLayout title="Meetings | Multiplayer">
      <div className={TranscriptStyle}>
        <div
          className={css`
            display: block;
            @media (max-width: 768px) {
              display: block;
            }
          `}
        >
          <Text.H3>Find all your previous Meeting transcripts here</Text.H3>
          <Separator.Horizontal spacing={4} />
        </div>
        <div className={TranscriptFilterContainerStyle}>
          <DatePicker
            onChange={onDateChange}
            value={value}
            maxDate={next2Weeks}
            maxDetail="month"
            calendarIcon={<CalendarBlankIcon fill="primary" />}
            clearIcon={<CloseIcon fill="#fff" />}
          />
        </div>
        <Separator.Vertical spacing={4} />
        <CardBase className={MeetingListCardStyle}>
          {(loading || meetingLoading) && <Text.Body>Loading...</Text.Body>}
          {all.map((t, idx, all) => (
            <React.Fragment key={t.transcriptId || t.meetingId}>
              <TranscriptListItem
                title={t.title || "Ad hoc meeting"}
                hasTranscript={t.hasTranscript}
                startTime={
                  t.meetingDate
                    ? format(new Date(t.meetingDate), "EEEE, MMMM d • hh:mm a")
                    : "Unknown"
                }
                onClick={() => {
                  if (t.disabled) {
                    return;
                  }
                  if (!t.id) {
                    return;
                  }
                  handleClick(t.id, t.hasTranscript, t.internal);
                }}
              />
              {idx !== all.length - 1 && (
                <Separator.Horizontal border spacing={1} />
              )}
            </React.Fragment>
          ))}
          {!all.length && !loading && (
            <Text.Body>Nothing to show you there</Text.Body>
          )}
        </CardBase>
      </div>
    </PageLayout>
  );
};
